import { useCallback, useMemo } from "react";
import { Message, MessagePrev } from "@/types";
import { resolveTabbedDisplayText } from "../helpers/messageHelpers";
import useChatStore from "../stores/chatStore";
import {
  buildLessonIdsForBadges,
  identifyContentKind,
  infoPanelBadgeLabel,
  withTabbedFromLiveChat,
} from "../helpers/contentKind";

export type InfoPanelBadgeLabel = "Lesson" | "Crash Course" | "Message";

export interface UseInfoPanelListRowHelpersParams {
  // Rows currently shown in the panel (favorites, filtered reactions, tag list, …).
  panelRows: MessagePrev[];
  messages: Message[];
  lessons: MessagePrev[];
  crashCourses: MessagePrev[];
}

export function useInfoPanelListRowHelpers({
  panelRows,
  messages,
  lessons,
  crashCourses,
}: UseInfoPanelListRowHelpersParams) {
  const {
    lessonTitleCache,
    favoriteTabLabelsCache,
    lessonsByScore,
    lessonsByCompetence,
    lessonsToDo,
    reactedLessonCache,
  } = useChatStore();

  const titleSources = useMemo(() => [...messages, ...lessons], [messages, lessons]);

  const crashCourseIds = useMemo(
    () => new Set(crashCourses.map((c) => c.message_id)),
    [crashCourses],
  );

  const lessonIds = useMemo(
    () =>
      buildLessonIdsForBadges(
        [lessons, lessonsByScore, lessonsByCompetence, lessonsToDo],
        messages,
        reactedLessonCache,
        favoriteTabLabelsCache,
        panelRows,
      ),
    [
      lessons,
      lessonsByScore,
      lessonsByCompetence,
      lessonsToDo,
      messages,
      reactedLessonCache,
      favoriteTabLabelsCache,
      panelRows,
    ],
  );

  const getTabbedText = useCallback(
    (msg: MessagePrev): string =>
      resolveTabbedDisplayText(msg, {
        lessonTitleCache,
        sources: titleSources,
        tabLabelsCache: favoriteTabLabelsCache,
      }),
    [lessonTitleCache, titleSources, favoriteTabLabelsCache],
  );

  const getBadgeLabel = useCallback(
    (msg: MessagePrev): InfoPanelBadgeLabel => {
      const row = withTabbedFromLiveChat(msg, messages);
      return infoPanelBadgeLabel(
        identifyContentKind(row, {
          lessonIds,
          crashCourseIds,
          favoriteTabLabelsCache,
        }),
      );
    },
    [messages, lessonIds, crashCourseIds, favoriteTabLabelsCache],
  );

  return { getTabbedText, getBadgeLabel };
}
